// ============================================================================
// Componente: ContabilidadeAutomacoes.js
// Objetivo: Central de automações dentro do Módulo Contabilidade — agrupa as
//           ferramentas P&L (Z-Score) e Conferência de Vendas como sub-abas, no
//           mesmo padrão do FinanceiroAutomacoes. O RTB12 fica de fora (é tile
//           próprio do módulo, estilo Petty Cash).
//
// ⚠️ O card IMOBILIZADO foi ARQUIVADO em 2026-09-01 (uso medido: 32 execuções,
//    todas em 14/07/2026). O código está em backup/contabilidade/imobilizado/ —
//    ver docs/PLANO.md › Trilho A › "Arquivar funcionalidade em desuso".
// Padrão visual: Contábil / Verde
// Conteúdo embutido: este componente é renderizado DENTRO do casco Contabilidade.js
//           (que já traz Sidebar + cabeçalho), então não desenha casco próprio.
// ============================================================================

import React, { useState } from 'react';
import {
    ArrowRight, Lock, CheckCircle2,
    TrendingUp, FileSearch
} from 'lucide-react';

import AutomacaoPL from './ContabilidadeAutomacaoPL';
import AutomacaoConferenciaVendas from './ContabilidadeAutomacaoConferenciaVendas';

const ContabilidadeAutomacoes = () => {
    const [activeSubTab, setActiveSubTab] = useState('visao-geral');

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const perfilUsuario = user?.perfil || '';

    // Permissões por ferramenta — mesmos perfis que acessavam as telas no hub antigo.
    const canAcessarPL = ['Contabil', 'Admin', 'Gerente Adm', 'Diretoria'].includes(perfilUsuario);
    const canAcessarConferencia = ['Contabil', 'Admin', 'Gerente Adm', 'Diretoria'].includes(perfilUsuario);

    const corPrincipal = '#16a34a';
    const corPrincipalEscura = '#15803d';
    const corFundoClaro = '#dcfce7';
    const corFundoMaisClaro = '#f0fdf4';

    const tabStyle = (isActive) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 18px',
        backgroundColor: isActive ? corFundoClaro : 'transparent',
        color: isActive ? corPrincipal : '#64748b',
        border: isActive ? `1px solid ${corPrincipal}` : '1px solid transparent',
        borderRadius: '8px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s',
        fontSize: '14px'
    });

    const renderSubTab = () => {
        switch (activeSubTab) {
            case 'pl':
                return <AutomacaoPL embedded={true} />;

            case 'conferencia':
                return <AutomacaoConferenciaVendas embedded={true} />;

            case 'visao-geral':
            default:
                return <VisaoGeralAutomacoes />;
        }
    };

    const mudarAba = (aba, permitido = true) => {
        if (!permitido) return;
        setActiveSubTab(aba);
    };

    const AutomationCard = ({ id, title, desc, icon, tag, isAllowed = true, disabled = false }) => {
        const permitido = isAllowed && !disabled;

        return (
            <div
                onClick={() => mudarAba(id, permitido)}
                style={{
                    backgroundColor: permitido ? '#fff' : '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '16px',
                    padding: '24px',
                    cursor: permitido ? 'pointer' : 'not-allowed',
                    opacity: permitido ? 1 : 0.65,
                    minHeight: '220px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)',
                    transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => {
                    if (!permitido) return;
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 20px -8px rgba(22, 163, 74, 0.35)';
                    e.currentTarget.style.borderColor = corPrincipal;
                }}
                onMouseLeave={(e) => {
                    if (!permitido) return;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.04)';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                }}
            >
                <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    backgroundColor: permitido ? corPrincipal : '#cbd5e1'
                }} />

                {!permitido && (
                    <div style={{ position: 'absolute', top: '16px', right: '16px', color: '#94a3b8' }}>
                        <Lock size={20} />
                    </div>
                )}

                <div>
                    <div style={{
                        width: '52px',
                        height: '52px',
                        borderRadius: '14px',
                        backgroundColor: permitido ? corFundoMaisClaro : '#f1f5f9',
                        color: permitido ? corPrincipal : '#94a3b8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '18px'
                    }}>
                        {icon}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <h3 style={{ margin: 0, fontSize: '19px', fontWeight: '800', color: '#0f172a' }}>
                            {title}
                        </h3>

                        {tag && (
                            <span style={{
                                fontSize: '11px',
                                fontWeight: '800',
                                color: corPrincipalEscura,
                                backgroundColor: corFundoClaro,
                                borderRadius: '999px',
                                padding: '4px 8px'
                            }}>
                                {tag}
                            </span>
                        )}
                    </div>

                    <p style={{ margin: 0, color: '#64748b', fontSize: '14px', lineHeight: '1.6' }}>
                        {desc}
                    </p>
                </div>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '22px',
                    color: permitido ? corPrincipal : '#94a3b8',
                    fontSize: '14px',
                    fontWeight: '800'
                }}>
                    {permitido ? (
                        <>
                            <ArrowRight size={16} />
                            Acessar automação
                        </>
                    ) : (
                        <>
                            <Lock size={16} />
                            {disabled ? 'Em implantação' : 'Acesso bloqueado'}
                        </>
                    )}
                </div>
            </div>
        );
    };

    const VisaoGeralAutomacoes = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: '20px'
            }}>
                <AutomationCard
                    id="pl"
                    title="Análise de P&L (Z-Score)"
                    tag="Estatística"
                    desc="Identificação estatística de outliers e ineficiências operacionais, com relatórios PDF executivos."
                    icon={<TrendingUp size={30} />}
                    isAllowed={canAcessarPL}
                />

                <AutomationCard
                    id="conferencia"
                    title="Conferência de Vendas"
                    tag="Balancete × SOVOS"
                    desc="Audita e cruza valores do Balancete vs Sistema SOVOS, com detalhamento analítico das divergências."
                    icon={<FileSearch size={30} />}
                    isAllowed={canAcessarConferencia}
                />
            </div>
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                borderBottom: '1px solid #e2e8f0',
                paddingBottom: '16px'
            }}>
                <button onClick={() => setActiveSubTab('visao-geral')} style={tabStyle(activeSubTab === 'visao-geral')}>
                    <CheckCircle2 size={18} />
                    Visão Geral
                </button>

                <button onClick={() => setActiveSubTab('pl')} style={tabStyle(activeSubTab === 'pl')}>
                    <TrendingUp size={18} />
                    Análise de P&L
                </button>

                <button onClick={() => setActiveSubTab('conferencia')} style={tabStyle(activeSubTab === 'conferencia')}>
                    <FileSearch size={18} />
                    Conferência de Vendas
                </button>
            </div>

            <div style={{ paddingTop: '8px' }}>
                {renderSubTab()}
            </div>
        </div>
    );
};

export default ContabilidadeAutomacoes;
